/**
 * Typed client for the TripPool API, derived from `@trippool/contracts`.
 *
 * The point of deriving it rather than hand-writing it is drift. A hand-written client is a second
 * declaration of the API's shape, and the second one is always the one that is wrong — it is updated
 * when someone remembers. Here every method's parameters and return type come from the same zod
 * schema the server validates with, so a changed field is a compile error in the client rather than
 * an `undefined` at runtime on a user's phone.
 *
 * Two behaviours are unusual enough to state outright:
 *
 * **Responses are validated.** A response is parsed with the contract's schema before it is returned.
 * That is CPU the server already spent, spent again on the client — but it converts "the deployed
 * server and this build disagree" from a mystery several frames deep into one clear error at the
 * boundary. It can be switched off; see `validateResponses`.
 *
 * **A write is never retried without an idempotency key.** Retrying a `POST /places` after a timeout
 * is how one place becomes two, and on a Vietnamese mobile connection a timeout is routine rather
 * than exceptional. The retry predicate refuses to retry a non-idempotent route unless the caller
 * supplied a key that lets the server collapse the replay.
 */

import {
  CreateFundParticipantRequest,
  CreatePlansRequest,
  CreatePlansResponse,
  CreateTripRequest,
  FundEntry,
  FundParticipant,
  RecordFundContributionRequest,
  RecordFundExpenseRequest,
  TripFundResponse,
  UpdateFundEntryRequest,
  UpdateFundParticipantRequest,
  PlanExplanationsResponse,
  PlanVariant,
  PlanVariantDetail,
  PutDayOrderRequest,
  SelectPlanRequest,
  TripPage,
  TripPlansResponse,
  TripResponse,
  TripRoutesResponse,
  TripWeatherResponse,
  UpdateTripRequest,
  AreaList,
  AreaResponse,
  AuthSession,
  CreateAreaRequest,
  GuestClaimRequest,
  GoogleSignInRequest,
  MeResponse,
  PasswordLoginRequest,
  PasswordRegisterRequest,
  RefreshRequest,
  SessionList,
  UpdateMeRequest,
  AiPlaceLookupRequest,
  AiPlaceLookupResponse,
  CreatePlaceRequest,
  CreatePlaceResponse,
  DeletePlaceResponse,
  DuplicateCheckRequest,
  DuplicateCheckResponse,
  DuplicatePairList,
  MergePlaceRequest,
  MergePlaceResponse,
  MetaResponse,
  Place,
  PlaceListQuery,
  PlaceLookupResponse,
  ResolveMapLinkResponse,
  PlaceSummaryPage,
  ROUTES,
  RoutePreviewRequest,
  RoutePreviewResponse,
  UpdatePlaceRequest,
  type Problem,
  type RouteDefinition,
} from "@trippool/contracts";
import type { z } from "zod";

import { ApiError, type ApiErrorInit } from "./errors.ts";

// ------------------------------------------------------------------------------------- config

export interface RetryPolicy {
  /** Total attempts, including the first. 1 means no retry. */
  attempts: number;
  /** Delay before the second attempt. Each later delay doubles, then is jittered. */
  baseDelayMs: number;
  /** Upper bound on one delay, so `retry_after` cannot make a request hang for minutes. */
  maxDelayMs: number;
}

export const DEFAULT_RETRY: RetryPolicy = {
  attempts: 3,
  baseDelayMs: 300,
  // Five seconds. A free-tier `retry_after` can be longer, and honouring a long one would look to
  // the user like a hang; better to fail and let them retry deliberately.
  maxDelayMs: 5_000,
};

export interface ApiClientConfig {
  /** Root including the version, e.g. `https://trippool.app/api/v1`. */
  baseUrl: string;
  /**
   * Returns the current access token, or null when there is none.
   *
   * A function rather than a string, because a token is refreshed while the client is alive and a
   * captured string would go stale — which shows up as a 401 loop after the first expiry.
   */
  getToken?: () => string | null | Promise<string | null>;
  /**
   * Called once on a 401, then the request is retried once.
   *
   * This is where a refresh token is exchanged. It returning without a new token makes the 401 the
   * final answer, so a client with nothing to refresh with does not loop.
   */
  onUnauthorized?: () => void | Promise<void>;
  /** Defaults to the global `fetch`. Injectable so tests run without a network. */
  fetch?: typeof globalThis.fetch;
  retry?: Partial<RetryPolicy>;
  /**
   * Validate each response against the contract.
   *
   * Default true. The cost is a parse of a payload the client is about to read anyway; the benefit is
   * that a server that changed without this client changing fails here, by name, instead of somewhere
   * downstream as a missing field.
   */
  validateResponses?: boolean;
  /** Sent as `User-Agent` where the runtime allows it. */
  userAgent?: string;
  /** Extra headers on every request, e.g. a trace id. */
  headers?: () => Record<string, string>;
}

// ------------------------------------------------------------------------------ call signatures

/**
 * The client's surface, one method per route.
 *
 * The names are the `operationId`s in the registry, so this interface and the server's routes are
 * keyed by the same strings. `client.test.ts` asserts the two sets are equal in both directions —
 * a route with no method and a method with no route both fail, which is what keeps this list from
 * becoming the stale second declaration it is trying to avoid.
 */
export interface ApiClient {
  /** What this server supports and what changed. Works without a valid session. */
  getMeta(options?: CallOptions): Promise<z.output<MetaResponseSchema>>;

  listPlaces(query?: ListPlacesQuery, options?: CallOptions): Promise<z.output<PlaceSummaryPageSchema>>;
  createPlace(body: CreatePlaceBody, options?: CallOptions): Promise<z.output<CreatePlaceResponseSchema>>;
  getPlace(params: { id: string }, options?: CallOptions): Promise<z.output<PlaceSchema>>;
  updatePlace(
    params: { id: string },
    body: UpdatePlaceBody,
    options?: CallOptions,
  ): Promise<z.output<PlaceSchema>>;
  deletePlace(params: { id: string }, options?: CallOptions): Promise<z.output<DeletePlaceResponseSchema>>;
  lookupPlace(query: LookupPlaceQuery, options?: CallOptions): Promise<z.output<PlaceLookupResponseSchema>>;
  /**
   * Look up place details and coordinates using Gemini with Google Search grounding.
   */
  aiLookupPlace(body: AiPlaceLookupBody, options?: CallOptions): Promise<z.output<AiPlaceLookupResponseSchema>>;
  /**
   * Resolve a Google Maps link into the coordinate it carries.
   *
   * Read `status` rather than treating a null `location` as an answer: `no_coordinate` means the link
   * itself holds nothing, while `unavailable` means Google could not be reached. Only the second is
   * worth retrying.
   */
  resolveMapLink(query: ResolveMapLinkQuery, options?: CallOptions): Promise<z.output<ResolveMapLinkResponseSchema>>;

  /**
   * The caller's own places a draft may duplicate, before it is saved.
   *
   * Empty is a definite no — the rule ran and matched nothing. A draft with no coordinate is refused
   * by the contract rather than answered by name alone, because proximity is half the rule and a
   * name-only match would be a guess presented as a fact (AC-3.5).
   */
  checkPlaceDuplicates(body: DuplicateCheckBody, options?: CallOptions): Promise<z.output<DuplicateCheckResponseSchema>>;
  /**
   * Pairs among the caller's saved places that the rule can see.
   *
   * A partial list by nature: name plus proximity cannot see a respelling, a street suffix, an
   * abbreviation or a place saved without coordinates. It is a starting point for review, not a
   * claim that the list holds every duplicate.
   */
  listDuplicatePairs(options?: CallOptions): Promise<z.output<DuplicatePairListSchema>>;
  /**
   * Fold `duplicate_place_id` into this place. The duplicate is soft-deleted, its sources, areas and
   * the caller's own rating move to the survivor, in one transaction.
   */
  mergePlace(params: { id: string }, body: MergePlaceBody, options?: CallOptions): Promise<z.output<MergePlaceResponseSchema>>;

  /**
   * The caller's own area vocabulary.
   *
   * An area is the user's word for a part of the world, not a geographic unit — which is why creating
   * one is idempotent on the folded name. `?q=` is folded-matched, so `cau dat` finds `Cầu Đất`.
   */
  listAreas(query?: { q?: string }, options?: CallOptions): Promise<AreaListOutput>;
  /** Add an area, or get the existing one. `created` says which happened. */
  createArea(body: CreateAreaBody, options?: CallOptions): Promise<AreaResponseOutput>;
  // --------------------------------------------------------------------------------- identity
  /**
   * Start as a guest.
   *
   * On the web the refresh token arrives as an `httpOnly` cookie and `refresh_token` is null; a
   * native client gets the value in the body. `refresh_token_transport` says which, so a client never
   * has to infer it from its own platform.
   */
  signInAsGuest(options?: CallOptions): Promise<AuthSessionOutput>;
  signInWithGoogle(body: GoogleSignInBody, options?: CallOptions): Promise<AuthSessionOutput>;
  registerWithPassword(body: PasswordRegisterBody, options?: CallOptions): Promise<AuthSessionOutput>;
  loginWithPassword(body: PasswordLoginBody, options?: CallOptions): Promise<AuthSessionOutput>;
  /** Attach credentials to the current guest. The user row and everything it owns are kept (AC-1.2). */
  claimGuestAccount(body: GuestClaimBody, options?: CallOptions): Promise<AuthSessionOutput>;
  /**
   * Rotate the refresh token.
   *
   * Pass no body on the web — the token is in the cookie. A native client passes `refresh_token`.
   * Reusing an already-spent token revokes every session in its rotation family, so a caller should
   * not retry this blindly on a network error.
   */
  refreshSession(body?: RefreshBody, options?: CallOptions): Promise<AuthSessionOutput>;
  logout(options?: CallOptions): Promise<void>;
  logoutAll(options?: CallOptions): Promise<void>;
  listSessions(options?: CallOptions): Promise<SessionListOutput>;
  revokeSession(params: { id: string }, options?: CallOptions): Promise<void>;

  // ---------------------------------------------------------------------------------- profile
  getMe(options?: CallOptions): Promise<MeOutput>;
  updateMe(body: UpdateMeBody, options?: CallOptions): Promise<MeOutput>;
  /** Irreversible. Everything the account owns is deleted, and any place it shared survives unowned. */
  deleteMe(options?: CallOptions): Promise<void>;

  // ------------------------------------------------------------------------------------- trips
  listTrips(query?: { limit?: number; cursor?: string }, options?: CallOptions): Promise<TripPageOutput>;
  /** A trip can exist before it has places; constraints default to what a first-time user would choose. */
  createTrip(body: CreateTripBody, options?: CallOptions): Promise<TripResponseOutput>;
  getTrip(params: { id: string }, options?: CallOptions): Promise<TripResponseOutput>;
  /** `place_ids` replaces the selection rather than adding to it. */
  updateTrip(params: { id: string }, body: UpdateTripBody, options?: CallOptions): Promise<TripResponseOutput>;
  deleteTrip(params: { id: string }, options?: CallOptions): Promise<void>;

  // ------------------------------------------------------------------------------------- plans
  /**
   * Generate plan variants.
   *
   * Every metric is nullable and `null` means the routing provider did not answer — a caller renders
   * "chưa xác định" rather than substituting an estimate. `routing_status` says whether the provider
   * itself was reachable, so an empty `variants` is never read as "no plan exists".
   */
  createPlans(params: { id: string }, body?: CreatePlansBody, options?: CallOptions): Promise<CreatePlansOutput>;
  /**
   * The variants a trip already has.
   *
   * This is what makes a saved trip openable: without it the only way to see an itinerary was to
   * generate it again. Empty means the trip has no plans yet, which is a `draft` and not a failure.
   */
  listPlanVariants(params: { id: string }, options?: CallOptions): Promise<TripPlansOutput>;
  getPlanVariant(params: { id: string; variantId: string }, options?: CallOptions): Promise<PlanVariantDetailOutput>;
  selectPlan(
    params: { id: string; variantId: string },
    body?: SelectPlanBody,
    options?: CallOptions,
  ): Promise<PlanVariantOutput>;
  /** Every reason carries its evidence; a reason without evidence is not returned at all. */
  getPlanExplanations(
    params: { id: string; variantId: string },
    options?: CallOptions,
  ): Promise<PlanExplanationsOutput>;
  /** One day's road path. `distances_known: false` means draw markers without a line. */
  getDayRoute(
    params: { id: string; variantId: string; dayIndex: number },
    options?: CallOptions,
  ): Promise<TripRoutesOutput>;

  /** The complete new order for one day. A partial list is rejected. */
  putDayOrder(
    params: { id: string; variantId: string; dayIndex: number },
    body: PutDayOrderBody,
    options?: CallOptions,
  ): Promise<void>;

  /** `out_of_range` is a normal case past the forecast horizon, not an outage. */
  getTripWeather(params: { id: string }, options?: CallOptions): Promise<TripWeatherOutput>;

  // -------------------------------------------------------------------------------------- fund
  /**
   * The trip's fund: pot, totals, contributors and the ledger.
   *
   * `pot_vnd` is null when nothing is recorded — render "chưa ghi gì", never `0 đ` — and may be negative
   * when an expense over-draws the fund. Each entry's `pot_after` is the running figure the list shows.
   */
  getTripFund(params: { id: string }, options?: CallOptions): Promise<TripFundOutput>;
  /** Add a person who may contribute. A folded name that already exists is a 409, not a second person. */
  createFundParticipant(
    params: { id: string },
    body: CreateFundParticipantBody,
    options?: CallOptions,
  ): Promise<FundParticipantOutput>;
  /** Rename a contributor. The money is the ledger's and does not move. A duplicate folded name is a 409. */
  updateFundParticipant(
    params: { id: string; participantId: string },
    body: UpdateFundParticipantBody,
    options?: CallOptions,
  ): Promise<FundParticipantOutput>;
  /**
   * Remove a person from the fund panel. Refused with a 409 carrying the reason when they have any
   * transaction in the fund — whether a member may go is the server's fact, not this client's.
   */
  deleteFundParticipant(params: { id: string; participantId: string }, options?: CallOptions): Promise<void>;
  /** Money in, attributed to a participant of this trip. */
  recordFundContribution(
    params: { id: string },
    body: RecordFundContributionBody,
    options?: CallOptions,
  ): Promise<FundEntryOutput>;
  /** Money out. The fund may over-draw: the pot goes negative rather than refusing the expense. */
  recordFundExpense(
    params: { id: string },
    body: RecordFundExpenseBody,
    options?: CallOptions,
  ): Promise<FundEntryOutput>;
  /** Correct a contribution in place: `created_at` and position stand, `edited_at` is set. */
  updateFundContribution(
    params: { id: string; entryId: string },
    body: UpdateFundEntryBody,
    options?: CallOptions,
  ): Promise<FundEntryOutput>;
  deleteFundContribution(params: { id: string; entryId: string }, options?: CallOptions): Promise<void>;
  /** Correct an expense in place: `created_at` and position stand, `edited_at` is set. */
  updateFundExpense(
    params: { id: string; entryId: string },
    body: UpdateFundEntryBody,
    options?: CallOptions,
  ): Promise<FundEntryOutput>;
  deleteFundExpense(params: { id: string; entryId: string }, options?: CallOptions): Promise<void>;

  /**
   * A road path through points, for a day that has no plan yet.
   *
   * What the arrange step draws. `fixed` matters and is the caller's statement: `true` when the user gave
   * the day a time of day, so the order is an instruction rather than a starting point. The reply's `order`
   * is the order actually driven — apply it to the points you sent before numbering anything.
   */
  previewRoute(body: RoutePreviewBody, options?: CallOptions): Promise<RoutePreviewOutput>;
}

export interface CallOptions {
  /**
   * `Idempotency-Key` for a write.
   *
   * Supplying one is what makes a retry safe, and on the routes that advertise the header it is
   * worth supplying on any write the user can trigger twice — a double tap, a flaky network, a
   * resumed tab.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Override the client's retry policy for this call. */
  retry?: Partial<RetryPolicy>;
}
/*
 * Parameter and body types, taken from the contract.
 *
 * `z.input` and not `z.output`: these describe what a *caller* passes, so a field with a default is
 * optional here and required in the response. Using `output` would force every caller to supply
 * `confirm_duplicate` explicitly, and the server would then apply a default nobody wanted.
 */
type MetaResponseSchema = typeof MetaResponse;
type RoutePreviewOutput = z.output<typeof RoutePreviewResponse>;
type PlaceSummaryPageSchema = typeof PlaceSummaryPage;
type CreatePlaceResponseSchema = typeof CreatePlaceResponse;
type PlaceSchema = typeof Place;
type DeletePlaceResponseSchema = typeof DeletePlaceResponse;
type PlaceLookupResponseSchema = typeof PlaceLookupResponse;
type AiPlaceLookupResponseSchema = typeof AiPlaceLookupResponse;
type ResolveMapLinkResponseSchema = typeof ResolveMapLinkResponse;
type DuplicateCheckResponseSchema = typeof DuplicateCheckResponse;
type DuplicatePairListSchema = typeof DuplicatePairList;
type MergePlaceResponseSchema = typeof MergePlaceResponse;
type TripPageOutput = z.output<typeof TripPage>;
type TripResponseOutput = z.output<typeof TripResponse>;
type CreatePlansOutput = z.output<typeof CreatePlansResponse>;
type TripPlansOutput = z.output<typeof TripPlansResponse>;
type PlanVariantOutput = z.output<typeof PlanVariant>;
type PlanVariantDetailOutput = z.output<typeof PlanVariantDetail>;
type PlanExplanationsOutput = z.output<typeof PlanExplanationsResponse>;
type TripRoutesOutput = z.output<typeof TripRoutesResponse>;
type TripWeatherOutput = z.output<typeof TripWeatherResponse>;
type TripFundOutput = z.output<typeof TripFundResponse>;
type FundEntryOutput = z.output<typeof FundEntry>;
type FundParticipantOutput = z.output<typeof FundParticipant>;
type AreaListOutput = z.output<typeof AreaList>;
type AreaResponseOutput = z.output<typeof AreaResponse>;
type AuthSessionOutput = z.output<typeof AuthSession>;
type SessionListOutput = z.output<typeof SessionList>;
type MeOutput = z.output<typeof MeResponse>;

export type CreateFundParticipantBody = z.input<typeof CreateFundParticipantRequest>;
export type UpdateFundParticipantBody = z.input<typeof UpdateFundParticipantRequest>;
export type RecordFundContributionBody = z.input<typeof RecordFundContributionRequest>;
export type RecordFundExpenseBody = z.input<typeof RecordFundExpenseRequest>;
export type UpdateFundEntryBody = z.input<typeof UpdateFundEntryRequest>;
export type CreateTripBody = z.input<typeof CreateTripRequest>;
export type UpdateTripBody = z.input<typeof UpdateTripRequest>;
export type CreatePlansBody = z.input<typeof CreatePlansRequest>;
export type SelectPlanBody = z.input<typeof SelectPlanRequest>;
export type PutDayOrderBody = z.input<typeof PutDayOrderRequest>;
export type CreateAreaBody = z.input<typeof CreateAreaRequest>;
export type GoogleSignInBody = z.input<typeof GoogleSignInRequest>;
export type PasswordRegisterBody = z.input<typeof PasswordRegisterRequest>;
export type PasswordLoginBody = z.input<typeof PasswordLoginRequest>;
export type GuestClaimBody = z.input<typeof GuestClaimRequest>;
export type RefreshBody = z.input<typeof RefreshRequest>;
export type UpdateMeBody = z.input<typeof UpdateMeRequest>;

export type ListPlacesQuery = z.input<typeof PlaceListQuery>;
export type CreatePlaceBody = z.input<typeof CreatePlaceRequest>;
export type UpdatePlaceBody = z.input<typeof UpdatePlaceRequest>;
export type AiPlaceLookupBody = z.input<typeof AiPlaceLookupRequest>;
/**
 * The place lookup query.
 *
 * `lat`/`lon` are the search centre, and a caller that has one should always send it: the Vietnamese
 * provider will not answer without a centre, and the results' `distance_m` is measured from it. They are
 * optional on the wire so an older client keeps working — such a request is answered from the OSM path.
 */
export type LookupPlaceQuery = { q: string; area?: string; lat?: number; lon?: number };
export type ResolveMapLinkQuery = { url: string };
export type DuplicateCheckBody = z.input<typeof DuplicateCheckRequest>;
export type MergePlaceBody = z.input<typeof MergePlaceRequest>;
export type RoutePreviewBody = z.input<typeof RoutePreviewRequest>;

// ---------------------------------------------------------------------------------- internals

/** A route's runtime description plus the schemas its response and body are validated with. */
interface PreparedRoute extends RouteDefinition {
  /** Validates the success body. Absent for a bodyless status such as 204. */
  successSchema?: z.ZodType;
  /** The success status to expect, e.g. 200 or 201. */
  successStatus: number;
}

function prepare(routes: readonly RouteDefinition[]): Map<string, PreparedRoute> {
  const prepared = new Map<string, PreparedRoute>();
  for (const route of routes) {
    const entry = Object.entries(route.responses)
      .map(([status, spec]) => ({ status: Number(status), spec }))
      .find(({ status }) => status >= 200 && status < 300);
    if (entry === undefined) throw new Error(`${route.operationId} has no success response`);
    prepared.set(route.operationId, {
      ...route,
      successStatus: entry.status,
      ...(entry.spec.schema !== undefined ? { successSchema: entry.spec.schema } : {}),
    });
  }
  return prepared;
}

/** Substitute `:name` from a params object, refusing an unresolved placeholder. */
function buildPath(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_match, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === null) {
      // Better to throw here than to send `/places/undefined` and get a 404 that reads like a
      // missing record.
      throw new TypeError(`Missing path parameter "${name}" for ${path}`);
    }
    return encodeURIComponent(String(value));
  });
}

/**
 * Serialize query values.
 *
 * `undefined` and `null` are omitted rather than sent as the strings "undefined"/"null" — a bug that
 * reaches the server as a filter matching nothing, and reads as "no results" instead of "bad
 * request". Arrays repeat the key, which is how this API expresses a multi-value filter.
 */
function buildQuery(query: Record<string, unknown> | undefined): string {
  if (query === undefined) return "";
  const parts: URLSearchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) parts.append(key, String(item));
      }
      continue;
    }
    parts.append(key, String(value));
  }
  const serialized = parts.toString();
  return serialized === "" ? "" : `?${serialized}`;
}

/** Whether a failure may be retried. */
function isRetryableStatus(status: number): boolean {
  // 429 and 503 are the API's own "come back later"; 502/504 are a proxy in front of it. A 500 is
  // not retried: if the server has a bug, three identical failures help nobody and cost three
  // rounds of latency.
  return status === 429 || status === 502 || status === 503 || status === 504;
}

function delayFor(attempt: number, policy: RetryPolicy, retryAfterSeconds: number | null): number {
  if (retryAfterSeconds !== null) {
    return Math.min(retryAfterSeconds * 1000, policy.maxDelayMs);
  }
  const backoff = policy.baseDelayMs * 2 ** attempt;
  // Jitter, so a fleet of clients that all failed at once does not return in lockstep and fail the
  // service again at the same instant.
  const jitter = backoff * 0.25 * Math.random();
  return Math.min(backoff + jitter, policy.maxDelayMs);
}

function sleep(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    /*
     * `unref` so a pending backoff does not hold the process open.
     *
     * A `setTimeout` keeps Node's event loop alive. During a retry that is right — the request is still
     * going to happen. But it also means a finished script or test run waits out the last backoff
     * before exiting, which is a hang from the outside: a test file that passes in 40 ms took over 100
     * seconds and was killed by a timeout. Unref-ing is safe because the only thing that would resolve
     * this promise is the timer itself, and the caller is awaiting it.
     */
    timer.unref?.();
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** Read `Retry-After` from a 429/503, which the API sets alongside the problem body. */
function retryAfterOf(response: Response): number | null {
  const header = response.headers.get("retry-after");
  if (header === null) return null;
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

// --------------------------------------------------------------------------------- the client

export function createApiClient(config: ApiClientConfig): ApiClient {
  const baseUrl = config.baseUrl.replace(/\/+$/, "");
  const doFetch = config.fetch ?? globalThis.fetch;
  if (typeof doFetch !== "function") {
    throw new TypeError("No fetch available. Pass config.fetch on a runtime without a global fetch.");
  }
  const policy: RetryPolicy = { ...DEFAULT_RETRY, ...config.retry };
  const validateResponses = config.validateResponses ?? true;
  const routes = prepare(ROUTES);

  async function readProblem(response: Response, url: string): Promise<Problem | null> {
    try {
      const body = (await response.json()) as unknown;
      // Not validated against the Problem union: if the server sent a malformed error body, the
      // caller still needs the status and the text, and throwing here would hide both.
      if (typeof body === "object" && body !== null && "type" in body && "status" in body) {
        return body as Problem;
      }
      return null;
    } catch {
      return null;
    } finally {
      void url;
    }
  }

  async function call(
    operationId: string,
    args: { params?: Record<string, unknown>; query?: Record<string, unknown>; body?: unknown },
    options: CallOptions,
  ): Promise<unknown> {
    const route = routes.get(operationId);
    if (route === undefined) throw new Error(`Unknown operation "${operationId}"`);

    const url = `${baseUrl}${buildPath(route.path, args.params)}${buildQuery(args.query)}`;
    const callPolicy: RetryPolicy = { ...policy, ...options.retry };

    /*
     * Retrying a write is only safe when the replay can be collapsed.
     *
     * A `GET` is retried freely. A write needs either the route to advertise `Idempotency-Key` and
     * the caller to have supplied one, or the route to be idempotent by construction. This is the
     * rule that stops a timeout on `POST /places` from creating the same place twice — and a
     * timeout is a normal event on a mobile connection, not an edge case.
     */
    const idempotencyKey = options.idempotencyKey ?? (route.idempotent === true ? crypto.randomUUID() : undefined);
    const canRetry = route.method === "get" || idempotencyKey !== undefined;
    const attempts = canRetry ? callPolicy.attempts : 1;

    let refreshed = false;

    /**
     * The token this attempt sent.
     *
     * Tracked so a 401 can tell "my token was rejected" from "somebody already replaced it". Those
     * need different responses, and conflating them is destructive here: refresh tokens are
     * single-use and a replay revokes every session the user has, so a second refresh triggered by a
     * 401 that was *already answered* by the first one signs the user out on every device.
     *
     * Not hypothetical. Four requests fired together all 401; the first refresh completes in
     * milliseconds while the other three are still processing their responses, so a per-call
     * "have I refreshed yet?" flag is false for each of them and each starts its own rotation. Found
     * by a test that counts refreshes, not by review.
     */
    let tokenSent: string | null = null;

    for (let attempt = 0; ; attempt++) {
      const headers: Record<string, string> = {
        Accept: "application/json",
        ...(config.headers?.() ?? {}),
      };
      if (config.userAgent !== undefined) headers["User-Agent"] = config.userAgent;
      if (idempotencyKey !== undefined) headers["Idempotency-Key"] = idempotencyKey;
      if (args.body !== undefined) headers["Content-Type"] = "application/json";

      const token = await config.getToken?.();
      tokenSent = token ?? null;
      if (token !== null && token !== undefined && token !== "") {
        headers["Authorization"] = `Bearer ${token}`;
      }

      let response: Response;
      try {
        response = await doFetch(url, {
          method: route.method.toUpperCase(),
          headers,
          ...(args.body !== undefined ? { body: JSON.stringify(args.body) } : {}),
          ...(options.signal !== undefined ? { signal: options.signal } : {}),
        });
      } catch (error) {
        // A network failure is the common case on a mobile connection, so it is retried like a
        // 503 rather than being thrown on the first attempt.
        const aborted = error instanceof DOMException && error.name === "AbortError";
        if (aborted || attempt + 1 >= attempts) {
          throw new ApiError({
            kind: "network",
            message: aborted ? "Yêu cầu đã bị huỷ." : "Không kết nối được máy chủ.",
            url,
            operationId,
            cause: error,
          });
        }
        await sleep(delayFor(attempt, callPolicy, null), options.signal);
        continue;
      }

      if (response.ok) {
        if (route.successStatus === 204 || response.status === 204) return undefined;
        const raw: unknown = await response.json();
        if (!validateResponses || route.successSchema === undefined) return raw;
        const parsed = route.successSchema.safeParse(raw);
        if (!parsed.success) {
          // The deployed server and this build disagree. Named explicitly, because the alternative is
          // a `undefined` somewhere downstream that has lost all connection to its cause.
          throw new ApiError({
            kind: "invalid-response",
            message: `Máy chủ trả về dữ liệu không khớp hợp đồng cho ${operationId}.`,
            url,
            operationId,
            status: response.status,
            detail: parsed.error.issues.map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`).join("; "),
          });
        }
        return parsed.data;
      }

      const retryAfterSeconds = retryAfterOf(response);

      /*
       * A 401 on a route that does not take a bearer token is terminal.
       *
       * `route.auth === false` is exactly the set of routes authenticated by something else or by
       * nothing: `/meta`, the sign-in endpoints, and `/auth/refresh` itself. Asking for a token refresh
       * in response to a failed refresh is a deadlock — `onUnauthorized` calls the shared refresh
       * promise, which is the very promise this call is inside, so it awaits itself and the request
       * never settles. The client hung on the most ordinary case there is: a browser with no refresh
       * cookie, whose first `POST /auth/refresh` returns 401 and whose promise never resolved.
       *
       * Found by a test that called `restore()` with a 401 and watched the process stop. It is the
       * reason the condition is keyed on the route's own `auth` flag rather than on the status alone:
       * a 401 from `/auth/password/login` cannot be fixed by refreshing either.
       */
      if (response.status === 401 && route.auth) {
        /*
         * If the token we sent is no longer the current one, another request already refreshed. This
         * 401 was answered before it arrived, so retrying with the new token is the whole fix — and
         * asking for a second refresh would present a refresh token the first one already spent.
         */
        const current = (await config.getToken?.()) ?? null;
        if (current !== null && current !== tokenSent) {
          continue;
        }

        if (!refreshed && config.onUnauthorized !== undefined) {
          refreshed = true;
          await config.onUnauthorized();
          continue;
        }
      }

      const problem = await readProblem(response, url);
      const lastAttempt = attempt + 1 >= attempts;
      if (!isRetryableStatus(response.status) || lastAttempt) {
        /*
         * `detail` before `title`, because that is the order RFC 9457 defines them in.
         *
         * `title` is the generic name of the problem class and does not vary: every 401 is "Chưa đăng
         * nhập", whether the token expired or a stolen refresh token was replayed. `detail` is the
         * explanation of *this* occurrence, and it is where "someone may have your credential, so
         * every session was ended" lives. Showing the title to a user hides the one message they
         * actually need to read.
         */
        const init: ApiErrorInit = {
          kind: "http",
          message: problem?.detail ?? problem?.title ?? `Máy chủ trả về ${response.status}.`,
          url,
          operationId,
          status: response.status,
        };
        if (problem !== null) init.problem = problem;
        if (problem?.detail !== undefined) init.detail = problem.detail;
        throw new ApiError(init);
      }

      await sleep(delayFor(attempt, callPolicy, retryAfterSeconds), options.signal);
    }
  }

  /*
   * The methods are built by walking the registry, not written out one by one.
   *
   * A hand-written set of method bodies would be a third place the API's shape is declared, after the
   * interface above and the registry. The registry already knows which arguments a route takes, so it
   * is asked rather than told: the leading arguments are whichever of params, query and body the
   * request declares, in that order, and the options object follows them.
   *
   * Deriving this matters more than it looks. A hard-coded arity table would be a second declaration
   * that is right on the day it is written — the first version of this file had one, and adding a
   * route with a query parameter would have silently mis-mapped its arguments, with the compiler
   * unable to help because the runtime indices are plain numbers.
   *
   * The cast at the end is the single place the derived surface meets the declared one. The test that
   * compares `keyof ApiClient` against the registry's operation ids in both directions is what makes
   * that cast safe.
   */
  const client: Record<string, unknown> = {};

  for (const [operationId, route] of routes) {
    const kinds: Array<"params" | "query" | "body"> = [];
    if (route.request?.params !== undefined) kinds.push("params");
    if (route.request?.query !== undefined) kinds.push("query");
    if (route.request?.body !== undefined) kinds.push("body");

    client[operationId] = (...incoming: unknown[]): Promise<unknown> => {
      const options = (incoming[kinds.length] ?? {}) as CallOptions;
      const args: { params?: Record<string, unknown>; query?: Record<string, unknown>; body?: unknown } = {};
      kinds.forEach((kind, index) => {
        args[kind] = incoming[index] as Record<string, unknown>;
      });
      return call(operationId, args, options);
    };
  }

  return client as unknown as ApiClient;
}

export { ApiError } from "./errors.ts";
export type { ApiErrorInit } from "./errors.ts";
